/**
 * React Query hook for fetching recent import items from timeline events.
 *
 * Fetches email subjects and meeting titles from integration timeline events,
 * providing real-time visibility into what's being imported.
 */

import { useQuery } from '@tanstack/react-query';

import { useGmailStatus } from '@/hooks/integrations/google/useGmailStatus';
import { useOutlookStatus } from '@/hooks/integrations/microsoft/useOutlookStatus';
import type { ImportProviderKind } from '@/hooks/integrations/useImportStatus';
import { fetchWithDefaults } from '@/lib/http/client';
import { queryKeys } from '@/lib/utils/queryKeyFactory';

export interface RecentImportItem {
  type: 'email' | 'calendar';
  name: string;
  detail: string;
}

/** Stable reference returned when the hook is disabled or no data is
 * available. Module-level so consumers that destructure `data` keep a
 * stable reference across polls (relevant for React.memo boundaries on
 * the dashboard parent — see DashboardHome.tsx). Do not mutate. */
const EMPTY_RECENT_IMPORT_ITEMS: RecentImportItem[] = [];

/** Global cap on the number of entity IDs polled per refetch. Bounds the
 * fan-out of parallel timeline requests regardless of how many Gmail/Outlook
 * accounts are connected. */
const MAX_POLLED_ENTITY_IDS = 4;

/** How many timeline events to fetch per entity. Must be much larger than the
 * number of items we display: during an active import only ~10% of timeline
 * events are displayable (measured live on a gcal import: 800 association_added
 * / 120 property_updated / 100 object_created in 10 minutes), so a shallow
 * fetch collapses the feed to one or two items that visibly ping-pong. */
const EVENTS_FETCH_LIMIT = 25;

/** Max items in the rotating feed. At the ActivityFeed default 2s rotation
 * this gives up to a 16s cycle before repeating. */
const MAX_FEED_ITEMS = 8;

interface UseRecentImportItemsOptions {
  orgId: string;
  enabled?: boolean;
  /**
   * Kinds whose imports are currently active — pass
   * `getActiveImportKinds(importStatus.services)` (memoized). Items of any
   * other kind are dropped so the ticker only shows what is genuinely
   * arriving right now (e.g. no email subjects from an already-finished
   * email import while only the calendar import is still running).
   */
  activeKinds: ImportProviderKind[];
  /**
   * Narrow the ticker to one connected account's own entities.
   *
   * Omit it for aggregate surfaces, which want everything arriving across the
   * user's accounts. Pass it from a per-account card: `activeKinds` is a *kind*
   * filter, not an account filter, so without this a card polls whichever
   * accounts happen to come first and every card shows the same items (#10435).
   *
   * A social account belongs to exactly one provider, so scoping empties the
   * other provider's candidate list — which is correct, and makes the shared
   * `MAX_POLLED_ENTITY_IDS` budget irrelevant in the scoped case (one account
   * contributes at most its mail + calendar entity).
   */
  socialAccountId?: number;
}

interface TimelineEvent {
  event_id: string;
  entity_id: string;
  entity_type_name: string;
  event_type_name: string;
  occurred_at: string;
  payload?: {
    subject?: string;
    from?: string;
    properties?: {
      ahoy_title?: string;
      ahoy_start_time?: string;
    };
  };
}

interface TimelineEventsResponse {
  events: TimelineEvent[];
  pagination: {
    has_more: boolean;
    limit: number;
  };
}

/**
 * Select which accounts to poll for a provider: the one named account when
 * scoped, or the first two (aggregate default) when unscoped. Pure — for
 * identical inputs it returns the same accounts in the same order every
 * time, so Gmail and Outlook can't silently diverge, and the derived
 * `entityIds` (and therefore cache identity for unscoped callers) stays
 * unchanged from before this was extracted.
 */
function selectAccounts<T extends { social_account_id: number | null }>(
  accounts: T[],
  socialAccountId: number | undefined
): T[] {
  return socialAccountId === undefined
    ? accounts.slice(0, 2)
    : accounts.filter(a => a.social_account_id === socialAccountId);
}

/**
 * Build the query key for a recent-import-items fetch.
 *
 * Mirrors `importStatusQueryKey` in useImportStatus.ts: org-wide fetches keep
 * the bare `['import-recent', orgId]` prefix they have always used, so
 * aggregate callers (ProposalCenter, CalendarPage, DashboardHome) continue to
 * share one cache entry and one poll. An account-scoped fetch appends the
 * account id, giving it its own entry.
 */
function recentImportItemsQueryKeyBase(
  orgId: string,
  socialAccountId: number | undefined
): (string | number)[] {
  return socialAccountId === undefined
    ? ['import-recent', orgId]
    : ['import-recent', orgId, socialAccountId];
}

/**
 * Map timeline events to RecentImportItem format.
 * Filters noise and extracts email/meeting details.
 */
function mapEventsToItems(
  events: TimelineEvent[],
  activeKinds: ReadonlySet<ImportProviderKind>
): RecentImportItem[] {
  const emailItems: RecentImportItem[] = [];
  const calendarItems: RecentImportItem[] = [];

  // Entities are fetched with include_associated=true, so the same event can
  // come back from more than one polled entity — dedup by event_id.
  const seenEventIds = new Set<string>();

  for (const event of events) {
    if (seenEventIds.has(event.event_id)) {
      continue;
    }
    seenEventIds.add(event.event_id);

    // Filter out integration entity noise
    if (event.entity_type_name === 'ahoy_external_integration') {
      continue;
    }

    // Handle email events — only while an email-kind import is active
    if (
      activeKinds.has('email') &&
      (event.event_type_name === 'ahoy_email_sent' ||
        event.event_type_name === 'ahoy_email_received')
    ) {
      const subject = event.payload?.subject?.trim();
      const from = event.payload?.from;

      // Skip events with missing/empty fields
      if (!subject || !from) {
        continue;
      }

      // Extract sender name using 3-step parsing (same as useImportStatus.ts:210-217)
      let senderName = from;
      const nameMatch = from.match(/^([^<]+)\s*</);
      if (nameMatch) {
        senderName = nameMatch[1].trim();
      } else if (from.includes('@')) {
        senderName = from.split('@')[0];
      }

      // Truncate subject at 45-char boundary
      const truncatedSubject =
        subject.length > 45 ? subject.slice(0, 45) + '…' : subject;

      emailItems.push({
        type: 'email',
        name: senderName,
        detail: truncatedSubject,
      });
    }

    // Handle meeting creation events — only while a calendar-kind import is active
    if (
      activeKinds.has('calendar') &&
      event.event_type_name === 'ahoy_object_created'
    ) {
      // Verify this is from a meeting entity (not integration entity)
      if (event.entity_type_name === 'ahoy_external_integration') {
        continue;
      }

      const title = event.payload?.properties?.ahoy_title?.trim();

      // Skip if missing or empty title
      if (!title) {
        continue;
      }

      // Format with date if available (same as useImportStatus.ts:234-241)
      let detail = title;
      const startTime = event.payload?.properties?.ahoy_start_time;
      if (startTime) {
        try {
          const date = new Date(startTime);
          const dateStr = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          });
          detail = `${title} - ${dateStr}`;
        } catch {
          // Keep just the title if date parsing fails
        }
      }

      calendarItems.push({
        type: 'calendar',
        name: '',
        detail,
      });
    }
  }

  // Interleave email and calendar items (same as useImportStatus.ts:251-265)
  const result: RecentImportItem[] = [];
  const maxItems = MAX_FEED_ITEMS;
  let emailIdx = 0;
  let calendarIdx = 0;

  // Alternate between types, starting with email
  while (
    result.length < maxItems &&
    (emailIdx < emailItems.length || calendarIdx < calendarItems.length)
  ) {
    if (emailIdx < emailItems.length) {
      result.push(emailItems[emailIdx++]);
    }
    if (result.length < maxItems && calendarIdx < calendarItems.length) {
      result.push(calendarItems[calendarIdx++]);
    }
  }

  return result;
}

/**
 * Hook for fetching recent import items from timeline events.
 *
 * Polls timeline events for all connected accounts, extracting email subjects
 * and meeting titles. Uses the same polling strategy as useImportStatus (5s interval).
 */
export function useRecentImportItems({
  orgId,
  enabled = true,
  activeKinds,
  socialAccountId,
}: UseRecentImportItemsOptions): {
  data: RecentImportItem[];
  isLoading: boolean;
  error: Error | null;
} {
  // Provider-status subqueries only make sense when an email/calendar import is
  // actively arriving. Gate them on activeKinds too (not just `enabled`, which is
  // driven by hasActiveImports and is true for a CRM-only import): a CRM import
  // yields no ticker kinds, so firing Gmail/Outlook status requests for it is pure
  // waste. The timeline query below is already gated on activeKinds.length > 0.
  const statusEnabled = enabled && activeKinds.length > 0;

  // Fetch Gmail and Outlook status to get entity IDs
  const { data: gmailStatus } = useGmailStatus(orgId, {
    enabled: statusEnabled,
  });
  const { data: outlookStatus } = useOutlookStatus(
    statusEnabled ? orgId : undefined
  );

  // Collect each provider's candidate entity IDs separately (first 2 accounts
  // per provider, mail + calendar each), then interleave them up to the global
  // cap. Gathering then interleaving — rather than filling Gmail first — keeps
  // the shared MAX_POLLED_ENTITY_IDS budget from being exhausted by Gmail
  // before Outlook is considered, so a mixed Gmail+Outlook user still sees
  // recent Outlook imports.
  // Scoped: exactly the named account (at most one matches, and only on its own
  // provider). Unscoped: the first 2 accounts per provider, as before.
  const gmailAccounts = gmailStatus?.accounts ?? [];
  const selectedGmailAccounts = selectAccounts(gmailAccounts, socialAccountId);
  const gmailIds: string[] = [];
  for (const account of selectedGmailAccounts) {
    if (account.gmail_entity_id) {
      gmailIds.push(account.gmail_entity_id);
    }
    if (account.gcal_entity_id) {
      gmailIds.push(account.gcal_entity_id);
    }
  }

  const outlookAccounts = outlookStatus?.accounts ?? [];
  const selectedOutlookAccounts = selectAccounts(
    outlookAccounts,
    socialAccountId
  );
  const outlookIds: string[] = [];
  for (const account of selectedOutlookAccounts) {
    if (account.outlook_mail_entity_id) {
      outlookIds.push(account.outlook_mail_entity_id);
    }
    if (account.outlook_calendar_entity_id) {
      outlookIds.push(account.outlook_calendar_entity_id);
    }
  }

  // Round-robin between providers so neither is starved by the shared cap. If
  // one provider has fewer IDs, the other fills the remaining budget.
  const entityIds: string[] = [];
  for (
    let i = 0;
    (i < gmailIds.length || i < outlookIds.length) &&
    entityIds.length < MAX_POLLED_ENTITY_IDS;
    i++
  ) {
    if (i < gmailIds.length && entityIds.length < MAX_POLLED_ENTITY_IDS) {
      entityIds.push(gmailIds[i]);
    }
    if (i < outlookIds.length && entityIds.length < MAX_POLLED_ENTITY_IDS) {
      entityIds.push(outlookIds[i]);
    }
  }

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.entityEvents.custom(
      ...recentImportItemsQueryKeyBase(orgId, socialAccountId),
      {
        entityIds,
        // Part of the key: when a kind finishes importing, its items must
        // drop out rather than being served from a stale cache entry.
        activeKinds,
      }
    ),
    queryFn: async () => {
      // Fetch from each entity in parallel with separate AbortControllers
      const fetchPromises = entityIds.map(async entityId => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        try {
          const response = await fetchWithDefaults(
            `/api/orgs/${orgId}/crm-events/entities/${entityId}/events/?include_associated=true&limit=${EVENTS_FETCH_LIMIT}`,
            { signal: controller.signal }
          );
          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`Entity fetch failed: ${response.status}`);
          }

          const data: TimelineEventsResponse = await response.json();
          return data.events;
        } catch {
          clearTimeout(timeoutId);
          // Graceful degradation: return empty array for failed entities
          return [];
        }
      });

      // Use Promise.allSettled to handle partial failures
      const results = await Promise.allSettled(fetchPromises);

      // Collect all successful timeline events
      const allEvents: TimelineEvent[] = [];
      for (const result of results) {
        if (result.status === 'fulfilled') {
          allEvents.push(...result.value);
        }
      }

      // Map events to items, scoped to the kinds actively importing
      return mapEventsToItems(allEvents, new Set(activeKinds));
    },
    enabled: enabled && entityIds.length > 0 && activeKinds.length > 0,
    staleTime: 2000,
    refetchInterval: enabled ? 5000 : false,
  });

  // Return empty array when disabled or on error (graceful degradation).
  // EMPTY_RECENT_IMPORT_ITEMS is a module-level constant so the destructured
  // `data` field stays referentially stable across calls — required for the
  // React.memo boundary on DashboardHomeComponent to be effective.
  if (!enabled || !data) {
    return {
      data: EMPTY_RECENT_IMPORT_ITEMS,
      isLoading,
      error: error as Error | null,
    };
  }

  return {
    data,
    isLoading,
    error: error as Error | null,
  };
}
